import cn from 'classnames';
import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { MedicalImageRender } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { toolbarMIRModel } from '@/entities/toolbarMIR';

import { useMaskEditState } from '../../hooks';

import styles from './MaskEditActions.module.scss';

type MaskEditActionsProps = {
  className?: string;
  testID?: string;
  isHiding?: boolean;
};

export const MaskEditActions: FC<MaskEditActionsProps> = (props) => {
  const { className, testID, isHiding } = props;

  const dispatch = useAppDispatch();

  const isEditingMask = useAppSelector(
    toolbarMIRModel.selectors.selectIsEditingMask('main'),
  );

  const { canReset, hasPendingChanges } = useMaskEditState({ isEditingMask });

  const closeEditMode = useCallback(() => {
    dispatch(
      toolbarMIRModel.actions.activateTool({
        toolbarID: 'main',
        control: 'editMask',
        value: false,
      }),
    );
  }, []);

  // NOTE: apply/cancel have to run before the tool is switched off — the redux
  // action reaches the render synchronously inside dispatch (middleware →
  // orchestrator → activateMode), so deactivating first would suspend the
  // session and roll the contour back before the engine command runs.
  const handleCancel = useCallback(() => {
    MedicalImageRender.cancelMaskEdit();
    closeEditMode();
  }, [closeEditMode]);

  const handleReset = useCallback(() => {
    MedicalImageRender.resetMaskEdit();
  }, []);

  const handleConfirm = useCallback(() => {
    MedicalImageRender.applyMaskEdit();
    closeEditMode();
  }, [closeEditMode]);

  return (
    <div
      data-testid={testID}
      data-component="mask-edit-actions"
      className={cn(styles.container, className, isHiding && styles.hiding)}
    >
      <button
        type="button"
        onClick={handleCancel}
        className={cn('p4', styles.button)}
      >
        <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
      </button>
      <button
        type="button"
        onClick={handleReset}
        disabled={!canReset}
        className={cn('p4', styles.button, styles.resetButton)}
      >
        <FormattedMessage id="global.reset" defaultMessage="Reset" />
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={!hasPendingChanges}
        className={cn('p4', styles.button, styles.confirmButton)}
      >
        <FormattedMessage id="global.confirm" defaultMessage="Confirm" />
      </button>
    </div>
  );
};
